import { useQuery, type QueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useUniversalAuth, useAuth } from "@wtc/ui";
import type { ProviderTimelineMessage } from "@/lib/types/email-sync";

interface UseProviderTimelineMessagesOptions {
  enabled?: boolean;
  limit?: number;
  daysBack?: number; // Number of days to look back, defaults to 30
}

const fetchProviderTimelineMessages = async (
  email: string,
  options: UseProviderTimelineMessagesOptions = {},
  token?: string
): Promise<ProviderTimelineMessage[]> => {
  if (!email || typeof email !== "string" || email.trim() === "") {
    return [];
  }

  const { limit = 100, daysBack = 30 } = options;

  // Calculate date range for the last N days
  const toDate = new Date();
  const fromDate = new Date();
  fromDate.setDate(fromDate.getDate() - daysBack);

  const searchParams = new URLSearchParams({
    email: email.trim(),
    limit: limit.toString(),
    fromDate: fromDate.toISOString(),
    toDate: toDate.toISOString(),
  });

  try {
    const url = `/api/connections/messages/timeline?${searchParams}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch provider timeline messages: ${response.statusText} - ${errorText}`
      );
    }

    const data = await response.json();

    // Transform the messages to match our interface
    // Handle both direct array format and wrapped format
    const rawMessages = Array.isArray(data) ? data : data.messages || [];
    const messages: ProviderTimelineMessage[] = rawMessages.map((msg: Record<string, unknown>) => {
      // Determine direction based on sender email
      // If the contact sent the message (sender === contact), it's inbound TO us
      // If we sent the message (sender !== contact), it's outbound FROM us
      const senderEmail = (msg.sender as Record<string, unknown> | undefined)?.email
        ?.toString()
        .toLowerCase();
      const contactEmail = email.toLowerCase();
      const direction = senderEmail === contactEmail ? "inbound" : "outbound";

      return {
        id:
          msg.id ||
          msg.providerMessageId ||
          `${msg.provider}-${msg.connectionId}-${msg.sentDateTime}`,
        subject: msg.subject || "(No Subject)",
        body: msg.body || msg.bodyPreview || "",
        sender: {
          email: (msg.sender as Record<string, unknown> | undefined)?.email?.toString() || "",
          name:
            (msg.sender as Record<string, unknown> | undefined)?.name?.toString() ||
            (msg.sender as Record<string, unknown> | undefined)?.displayName?.toString() ||
            (msg.sender as Record<string, unknown> | undefined)?.email?.toString(),
        },
        recipients: msg.recipients || [],
        sentAt: msg.sentDateTime || msg.receivedDateTime || new Date().toISOString(),
        provider: msg.provider,
        connectionId: msg.connectionId,
        inboxId: msg.inboxId || msg.connectionId,
        direction,
        messageType: "email", // Timeline API returns email messages
        status: msg.status || (msg.isRead ? "read" : "unread"),
        threadId: msg.conversationId,
        externalId: msg.providerMessageId || msg.id,
        attachments: msg.attachments || [],
      };
    });

    // Sort by sentAt date (newest first)
    messages.sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());

    return messages;
  } catch (error) {
    console.error("Error fetching provider timeline messages:", error);
    // Return empty array instead of throwing to prevent timeline from breaking
    return [];
  }
};

export const useProviderTimelineMessages = (
  email: string,
  options: UseProviderTimelineMessagesOptions = {}
) => {
  const { appUser, isAuthenticated } = useUniversalAuth();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  // Create stable query key to prevent unnecessary refetches
  const queryKey = useMemo(
    () => [
      "providerTimelineMessages",
      email,
      {
        limit: options.limit,
        daysBack: options.daysBack,
        enabled: options.enabled,
        userId: appUser?.id, // Include user ID to ensure user-specific caching
      },
    ],
    [email, options.limit, options.daysBack, options.enabled, appUser?.id]
  );

  const query = useQuery<ProviderTimelineMessage[], Error>({
    queryKey,
    queryFn: () => fetchProviderTimelineMessages(email, options, token || undefined),
    enabled:
      options.enabled !== false &&
      !!email &&
      typeof email === "string" &&
      email.trim() !== "" &&
      isAuthenticated &&
      !!appUser, // Ensure user is authenticated
    retry: 2, // Retry twice on failure
    staleTime: 5 * 60 * 1000, // 5 minutes - longer since messages don't change as frequently
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus for performance
  });

  return query;
};

// Export a prefetch function that can be used in server components or page load
export const prefetchProviderTimelineMessages = async (
  queryClient: QueryClient,
  email: string,
  userId: string | null,
  options: UseProviderTimelineMessagesOptions = {}
) => {
  // Only prefetch if we have a user ID (authentication context)
  if (!userId) {
    console.warn("Cannot prefetch provider messages without user authentication");
    return;
  }

  const queryKey = [
    "providerTimelineMessages",
    email,
    {
      limit: options.limit,
      daysBack: options.daysBack,
      enabled: options.enabled,
      userId, // Include user ID to match client-side query key
    },
  ];

  try {
    await queryClient.prefetchQuery({
      queryKey,
      queryFn: () => fetchProviderTimelineMessages(email, options),
      staleTime: 5 * 60 * 1000,
    });
  } catch (error) {
    console.warn("Failed to prefetch provider messages:", error);
    // Don't throw - allow the page to load without prefetched data
  }
};
